import type {
  AeBuilderQuestionType,
  AeQuestionDisplayConfig,
  AeQuestionType,
  CreateQuestionOptionInput,
} from '@/types/assessmentEngine';
import {
  defaultMatrixConfig,
  defaultNpsConfig,
  defaultScaleConfig,
} from './scaleUtils';

/** Question types shown in the template builder. */
export const BUILDER_QUESTION_TYPES: AeBuilderQuestionType[] = [
  'short_text',
  'long_text',
  'single_choice',
  'multi_choice',
  'scale',
  'nps',
  'matrix',
];

export const SINGLE_CHOICE_DISPLAYS = ['radio', 'dropdown', 'checkbox'] as const;
export const MULTI_CHOICE_DISPLAYS = ['checkbox', 'boxes'] as const;

export function toBuilderQuestionType(type: AeQuestionType): AeBuilderQuestionType {
  if (type === 'demographic' || type === 'short_text') return 'short_text';
  if (type === 'open_text' || type === 'long_text') return 'long_text';
  if (type === 'radio' || type === 'single_choice') return 'single_choice';
  if (type === 'multiple_choice' || type === 'multi_choice') return 'multi_choice';
  if (type === 'nps') return 'nps';
  if (type === 'matrix') return 'matrix';
  if (type === 'consent') return 'short_text';
  return 'scale';
}

export function fromBuilderQuestionType(type: AeBuilderQuestionType): AeQuestionType {
  return type;
}

export function questionNeedsOptions(type: AeBuilderQuestionType): boolean {
  return type === 'single_choice' || type === 'multi_choice' || type === 'matrix';
}

export function questionUsesScale(type: AeBuilderQuestionType): boolean {
  return type === 'scale' || type === 'nps' || type === 'matrix';
}

export function parseDisplayConfig(raw: unknown): AeQuestionDisplayConfig {
  if (!raw || typeof raw !== 'object') return {};
  return raw as AeQuestionDisplayConfig;
}

export function defaultDisplayConfig(type: AeBuilderQuestionType): AeQuestionDisplayConfig {
  if (type === 'single_choice') return { choiceDisplay: 'radio' };
  if (type === 'multi_choice') return { choiceDisplay: 'checkbox' };
  if (type === 'nps') return defaultNpsConfig();
  if (type === 'matrix') return defaultMatrixConfig();
  if (type === 'scale') return defaultScaleConfig();
  return {};
}

export function defaultScaleMin(type: AeBuilderQuestionType): number {
  return type === 'nps' ? 0 : 1;
}

export function defaultScaleMax(type: AeBuilderQuestionType): number {
  if (type === 'nps') return 10;
  return 5;
}

/** DB column ae_template_question_options.option_value is varchar(255). */
export const OPTION_VALUE_MAX_LENGTH = 255;

/**
 * Machine key for a choice option. Truncated so long labels never exceed the DB limit.
 * Matrix statements must NOT use this — they keep a stable ROW_n key while the full
 * statement lives only in option_label (each row is a question sharing the matrix scale).
 */
export function slugOptionValue(label: string, fallback: string): string {
  const slug = label
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const value = slug || fallback;
  return value.slice(0, OPTION_VALUE_MAX_LENGTH);
}

/** Next unused ROW_n key for a matrix statement (row = statement-question). */
export function nextMatrixRowKey(existingValues: string[]): string {
  const used = new Set(existingValues);
  let n = 1;
  while (used.has(`ROW_${n}`)) n += 1;
  return `ROW_${n}`;
}

/**
 * Matrix rows are statement-questions sharing the scale columns. Keep short stable
 * keys; never let a full statement become option_value (varchar(255)).
 */
export function normalizeMatrixOptions(
  options: CreateQuestionOptionInput[]
): CreateQuestionOptionInput[] {
  const result = options.map((opt, index) => ({
    ...opt,
    sort_order: opt.sort_order ?? index,
  }));
  const used = new Set<string>();

  // Pass 1: keep existing short unique keys so statement identity stays stable.
  for (const opt of result) {
    const key = (opt.option_value ?? '').trim();
    if (key && key.length <= OPTION_VALUE_MAX_LENGTH && !used.has(key)) {
      used.add(key);
      opt.option_value = key;
    } else {
      opt.option_value = '';
    }
  }

  // Pass 2: assign ROW_n only to rows that need a new key (oversized / empty / duplicate).
  for (const opt of result) {
    if (!opt.option_value) {
      const key = nextMatrixRowKey([...used]);
      opt.option_value = key;
      used.add(key);
    }
  }

  return result;
}

export function defaultOptionsForType(type: AeBuilderQuestionType): CreateQuestionOptionInput[] {
  if (type === 'matrix') {
    return [
      { option_value: 'ROW_1', option_label: 'Statement 1', sort_order: 0 },
      { option_value: 'ROW_2', option_label: 'Statement 2', sort_order: 1 },
    ];
  }
  if (type === 'single_choice' || type === 'multi_choice') {
    return [
      { option_value: 'OPTION_1', option_label: 'Option 1', sort_order: 0 },
      { option_value: 'OPTION_2', option_label: 'Option 2', sort_order: 1 },
    ];
  }
  return [];
}

export function draftDefaultsForType(type: AeBuilderQuestionType) {
  return {
    display_config: defaultDisplayConfig(type),
    scale_min: defaultScaleMin(type),
    scale_max: defaultScaleMax(type),
    options: defaultOptionsForType(type),
    allow_na: false,
  };
}

